import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Plus, Trash2, Save, AlertCircle, ArrowUp, ArrowDown, 
  FileText, Send, Clock, Eye, Sparkles, CheckCircle2, Edit2, Copy, Upload
} from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { Client } from '@/types/client';
import { AtomicLoadingScreen } from '@/components/ui/atomic-loader';
import { apiService } from '@/lib/api';

interface FlowMessage {
  order: number;
  type: 'TEXT' | 'FILE' | 'IMAGE';
  content: string;
  delay_after_seconds: number;
  file_column?: string;
  image_url?: string;
}

interface MessageFlow {
  id?: number;
  name: string;
  description?: string;
  client_id: number;
  connection_id: number;
  messages: FlowMessage[];
  delay_between_sends_seconds: number;
  is_active: boolean;
  connection?: {
    id: number;
    name: string;
    connection_type: string;
  };
}

interface Connection {
  id: number;
  name: string;
  connection_type: string;
  is_active: boolean;
}

interface Variable {
  name: string;
  description: string;
  type: string;
}

interface MessageFlowsTabProps {
  client: Client | null;
}

export default function MessageFlowsTab({ client }: MessageFlowsTabProps) {
  const [loading, setLoading] = useState(false);
  const [flows, setFlows] = useState<MessageFlow[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [selectedFlowId, setSelectedFlowId] = useState<number | null>(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  
  // Form fields
  const [flowName, setFlowName] = useState('');
  const [flowDescription, setFlowDescription] = useState('');
  const [selectedConnectionId, setSelectedConnectionId] = useState<number | null>(null);
  const [messages, setMessages] = useState<FlowMessage[]>([]);
  const [delayBetweenSends, setDelayBetweenSends] = useState(59);
  const [isActive, setIsActive] = useState(true);
  
  const [availableVariables, setAvailableVariables] = useState<Variable[]>([]);
  const [showVariableHelper, setShowVariableHelper] = useState(false);
  const [preview, setPreview] = useState<any>(null);
  const [uploadingImageForOrder, setUploadingImageForOrder] = useState<number | null>(null);

  useEffect(() => {
    if (client) {
      loadFlows();
      loadConnections();
      loadAvailableVariables();
    }
  }, [client]);

  useEffect(() => {
    if (selectedFlowId) {
      loadFlow(selectedFlowId);
    }
  }, [selectedFlowId]);

  const loadFlows = async () => {
    if (!client) return;
    
    try {
      const response = await apiService.get(`/message-flows/client/${client.id}`);
      console.log('[MessageFlowsTab] Response completo:', response);
      
      // Tratar diferentes formatos de resposta
      let flowsData = [];
      if (Array.isArray(response)) {
        flowsData = response;
      } else if (response.data && Array.isArray(response.data)) {
        flowsData = response.data;
      } else if (response.success && response.data) {
        flowsData = Array.isArray(response.data) ? response.data : [response.data];
      }
      
      console.log('[MessageFlowsTab] Fluxos encontrados:', flowsData.length);
      setFlows(flowsData);
    } catch (error) {
      console.error('Erro ao carregar fluxos:', error);
      toast({
        title: 'Erro',
        description: 'Falha ao carregar fluxos',
        variant: 'destructive'
      });
      setFlows([]); // Garantir que flows seja um array vazio em caso de erro
    }
  };

  const loadConnections = async () => {
    if (!client) return;
    
    try {
      const response = await apiService.get(`/whatsapp/client/${client.id}`);
      
      // Tratar diferentes formatos de resposta
      let connectionsData = [];
      if (Array.isArray(response)) {
        connectionsData = response;
      } else if (response.data && Array.isArray(response.data)) {
        connectionsData = response.data;
      } else if (response.connections && Array.isArray(response.connections)) {
        connectionsData = response.connections;
      }
      
      // Filtrar apenas conexões extra_oficial
      const filtered = connectionsData.filter((c: Connection) => c.connection_type === 'extra_oficial');
      console.log('[MessageFlowsTab] Conexões Extra Oficial:', filtered.length);
      setConnections(filtered);
    } catch (error) {
      console.error('Erro ao carregar conexões:', error);
      toast({
        title: 'Erro',
        description: 'Falha ao carregar conexões',
        variant: 'destructive'
      });
    }
  };

  const loadAvailableVariables = async () => {
    try {
      const response = await apiService.get('/message-flows/available-variables');
      setAvailableVariables(response.data);
    } catch (error) {
      console.error('Erro ao carregar variáveis:', error);
    }
  };

  const loadFlow = async (flowId: number) => {
    setLoading(true);
    try {
      const response = await apiService.get(`/message-flows/${flowId}`);
      
      if (response.success && response.data) {
        const flow = response.data;
        setFlowName(flow.name);
        setFlowDescription(flow.description || '');
        setSelectedConnectionId(flow.connection_id);
        setMessages(flow.messages || []);
        setDelayBetweenSends(flow.delay_between_sends_seconds || 59);
        setIsActive(flow.is_active);
      }
    } catch (error: any) {
      console.error('Erro ao carregar fluxo:', error);
      toast({
        title: 'Erro',
        description: 'Falha ao carregar fluxo',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNewFlow = () => {
    setIsCreatingNew(true);
    setSelectedFlowId(null);
    setFlowName('');
    setFlowDescription('');
    setSelectedConnectionId(null);
    setMessages([]);
    setDelayBetweenSends(59);
    setIsActive(true);
  };

  const handleSelectFlow = (flowId: number) => {
    setIsCreatingNew(false);
    setSelectedFlowId(flowId);
  };

  const handleDuplicateFlow = async (e: React.MouseEvent, flow: MessageFlow) => {
    e.stopPropagation();
    if (!client || !flow.id) return;

    setLoading(true);
    try {
      const response = await apiService.get(`/message-flows/${flow.id}`) as { data?: MessageFlow };
      const data = response?.data ?? response;
      const source = (data as MessageFlow)?.id ? (data as MessageFlow) : flow;

      const newName = (source.name || flow.name).trim().replace(/_copy(\d*)$/, '') + '_copy';

      setFlowName(newName);
      setFlowDescription(source.description || flow.description || '');
      setSelectedConnectionId(source.connection_id ?? flow.connection_id);
      setMessages(Array.isArray(source.messages) ? source.messages : (flow.messages || []));
      setDelayBetweenSends(source.delay_between_sends_seconds ?? flow.delay_between_sends_seconds ?? 59);
      setIsActive(source.is_active ?? flow.is_active ?? true);
      setIsCreatingNew(true);
      setSelectedFlowId(null);

      toast({
        title: 'Fluxo duplicado',
        description: `Edite o nome se quiser e clique em "Criar Fluxo" para salvar.`,
        variant: 'default'
      });
    } catch (error: any) {
      console.error('Erro ao duplicar fluxo:', error);
      toast({
        title: 'Erro',
        description: error.response?.data?.error || error.message || 'Falha ao duplicar fluxo',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const addMessage = () => {
    const newOrder = messages.length + 1;
    setMessages([
      ...messages,
      {
        order: newOrder,
        type: 'TEXT',
        content: '',
        delay_after_seconds: 3
      }
    ]);
  };

  const removeMessage = (order: number) => {
    const filtered = messages.filter(m => m.order !== order);
    // Reordenar
    const reordered = filtered.map((m, idx) => ({ ...m, order: idx + 1 }));
    setMessages(reordered);
  };

  const updateMessage = (order: number, field: keyof FlowMessage, value: any) => {
    setMessages(messages.map(m => 
      m.order === order ? { ...m, [field]: value } : m
    ));
  };

  const moveMessage = (order: number, direction: 'up' | 'down') => {
    const index = messages.findIndex(m => m.order === order);
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === messages.length - 1)
    ) {
      return;
    }

    const newMessages = [...messages];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    
    // Swap
    [newMessages[index], newMessages[swapIndex]] = [newMessages[swapIndex], newMessages[index]];
    
    // Reordenar
    const reordered = newMessages.map((m, idx) => ({ ...m, order: idx + 1 }));
    setMessages(reordered);
  };

  const handleUploadImage = async (messageOrder: number, file: File) => {
    if (!client) return;
    setUploadingImageForOrder(messageOrder);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiService.post(`/message-flows/client/${client.id}/upload-image`, formData);
      const imageUrl = (response as any)?.image_url;
      if (imageUrl) {
        updateMessage(messageOrder, 'image_url', imageUrl);
        toast({ title: 'Imagem enviada', description: 'A imagem foi enviada ao S3 com sucesso.', variant: 'default' });
      } else {
        throw new Error('URL não retornada');
      }
    } catch (error: any) {
      console.error('Erro no upload:', error);
      toast({
        title: 'Erro no upload',
        description: error.response?.data?.error || error.message || 'Falha ao enviar imagem',
        variant: 'destructive'
      });
    } finally {
      setUploadingImageForOrder(null);
    }
  };

  const insertVariable = (messageOrder: number, variableName: string) => {
    const message = messages.find(m => m.order === messageOrder);
    if (!message) return;

    const cursorPos = 0; // Simplified: add at end
    const newContent = `${message.content}{${variableName}}`;
    updateMessage(messageOrder, 'content', newContent);
  };

  const saveFlow = async () => {
    if (!client) return;

    // Validações
    if (!flowName.trim()) {
      toast({
        title: 'Erro',
        description: 'Nome do fluxo é obrigatório',
        variant: 'destructive'
      });
      return;
    }

    if (!selectedConnectionId) {
      toast({
        title: 'Erro',
        description: 'Selecione uma conexão',
        variant: 'destructive'
      });
      return;
    }

    if (messages.length === 0) {
      toast({
        title: 'Erro',
        description: 'Adicione pelo menos uma mensagem ao fluxo',
        variant: 'destructive'
      });
      return;
    }

    // Validar mensagens
    for (const msg of messages) {
      if (!msg.content.trim()) {
        toast({
          title: 'Erro',
          description: `Mensagem ${msg.order} está vazia`,
          variant: 'destructive'
        });
        return;
      }
      if (msg.type === 'FILE' && !msg.file_column) {
        toast({
          title: 'Erro',
          description: `Mensagem ${msg.order}: Selecione a coluna de arquivo`,
          variant: 'destructive'
        });
        return;
      }
      if (msg.type === 'IMAGE' && (!msg.image_url || !msg.image_url.startsWith('http'))) {
        toast({
          title: 'Erro',
          description: `Mensagem ${msg.order}: Envie uma imagem ou informe uma URL válida`,
          variant: 'destructive'
        });
        return;
      }
    }

    setLoading(true);
    try {
      const payload = {
        name: flowName,
        description: flowDescription,
        client_id: client.id,
        connection_id: selectedConnectionId,
        messages,
        delay_between_sends_seconds: delayBetweenSends,
        is_active: isActive
      };

      console.log('[MessageFlowsTab] Enviando payload:', payload);
      console.log('[MessageFlowsTab] isCreatingNew:', isCreatingNew);
      console.log('[MessageFlowsTab] selectedFlowId:', selectedFlowId);

      let response;
      if (isCreatingNew || !selectedFlowId) {
        // Criar novo fluxo
        console.log('[MessageFlowsTab] Criando novo fluxo via POST...');
        response = await apiService.post('/message-flows', payload);
        console.log('[MessageFlowsTab] Response CREATE:', response);
      } else {
        // Atualizar fluxo existente
        console.log('[MessageFlowsTab] Atualizando fluxo', selectedFlowId, 'via PUT...');
        response = await apiService.put(`/message-flows/${selectedFlowId}`, payload);
        console.log('[MessageFlowsTab] Response UPDATE:', response);
      }

      // Tratar diferentes formatos de resposta
      const isSuccess = response?.success !== false; // Se success não existe ou é true, consideramos sucesso
      
      console.log('[MessageFlowsTab] isSuccess:', isSuccess);
      
      if (isSuccess) {
        toast({
          title: 'Sucesso',
          description: isCreatingNew ? 'Fluxo criado com sucesso' : 'Fluxo atualizado com sucesso'
        });

        // Recarregar lista de fluxos
        await loadFlows();
        
        // Se foi criação, selecionar o novo fluxo
        const flowData = response?.data || response;
        if (isCreatingNew && flowData?.id) {
          setIsCreatingNew(false);
          setSelectedFlowId(flowData.id);
        }

        // Verificar warnings
        const warnings = response?.warnings || flowData?.warnings;
        if (warnings?.invalidVariables?.length > 0) {
          toast({
            title: 'Atenção',
            description: `Variáveis inválidas detectadas: ${warnings.invalidVariables.join(', ')}`,
            variant: 'default'
          });
        }
      } else {
        throw new Error(response?.error || 'Erro desconhecido ao salvar fluxo');
      }
    } catch (error: any) {
      console.error('Erro ao salvar fluxo:', error);
      toast({
        title: 'Erro',
        description: error.response?.data?.error || error.message || 'Falha ao salvar fluxo',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteFlow = async () => {
    if (!selectedFlowId) return;

    if (!confirm('Deseja realmente excluir este fluxo?')) return;

    setLoading(true);
    try {
      const response = await apiService.delete(`/message-flows/${selectedFlowId}`);
      
      // Tratar diferentes formatos de resposta
      const isSuccess = response?.success !== false;
      
      if (isSuccess) {
        toast({
          title: 'Sucesso',
          description: 'Fluxo excluído com sucesso'
        });

        // Limpar seleção e recarregar lista
        setSelectedFlowId(null);
        setIsCreatingNew(false);
        await loadFlows();
        handleNewFlow(); // Limpar formulário
      } else {
        throw new Error(response?.error || 'Erro ao excluir fluxo');
      }
    } catch (error: any) {
      console.error('Erro ao excluir fluxo:', error);
      toast({
        title: 'Erro',
        description: error.response?.data?.error || error.message || 'Falha ao excluir fluxo',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const generatePreview = async () => {
    if (!selectedFlowId || messages.length === 0) {
      toast({
        title: 'Atenção',
        description: 'Salve o fluxo primeiro para gerar preview',
        variant: 'default'
      });
      return;
    }

    setLoading(true);
    try {
      const response = await apiService.post(`/message-flows/${selectedFlowId}/preview`, {
        use_mock_data: true
      });

      if (response.success) {
        setPreview(response.data);
      }
    } catch (error: any) {
      console.error('Erro ao gerar preview:', error);
      toast({
        title: 'Erro',
        description: 'Falha ao gerar preview',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <AtomicLoadingScreen message="Carregando fluxos de mensagens..." />;
  }

  const selectedConnection = connections.find(c => c.id === selectedConnectionId);
  const currentFlow = flows.find(f => f.id === selectedFlowId);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Fluxos de Envio</h3>
          <p className="text-sm text-gray-600">
            Configure fluxos de mensagens personalizados para API Extra Oficial (Quepasa)
          </p>
        </div>
        <Button onClick={handleNewFlow} variant="default">
          <Plus className="w-4 h-4 mr-2" />
          Novo Fluxo
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lista de Fluxos */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-base">Fluxos Cadastrados</CardTitle>
            <CardDescription>Selecione um fluxo para editar</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {flows.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Nenhum fluxo cadastrado</p>
              </div>
            )}

            {flows.map(flow => (
              <div
                key={flow.id}
                className={`p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
                  selectedFlowId === flow.id ? 'border-blue-500 bg-blue-50' : ''
                }`}
                onClick={() => handleSelectFlow(flow.id!)}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{flow.name}</div>
                    {flow.description && (
                      <div className="text-xs text-gray-600 mt-1">{flow.description}</div>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant={flow.is_active ? 'default' : 'secondary'} className="text-xs">
                        {flow.is_active ? 'Ativo' : 'Inativo'}
                      </Badge>
                      <span className="text-xs text-gray-500">
                        {flow.messages?.length || 0} msg
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-gray-500 hover:text-blue-600"
                      onClick={(e) => handleDuplicateFlow(e, flow)}
                      title="Duplicar fluxo"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Edit2 className="w-4 h-4 text-gray-400" />
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Formulário de Edição */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">
              {isCreatingNew ? 'Novo Fluxo' : 'Editar Fluxo'}
            </CardTitle>
            <CardDescription>
              Configure as mensagens e delays do fluxo
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Informações Básicas */}
            <div className="space-y-4">
              <div>
                <Label>Nome do Fluxo *</Label>
                <Input
                  value={flowName}
                  onChange={(e) => setFlowName(e.target.value)}
                  placeholder="Ex: Lembrete 10 dias"
                />
              </div>

              <div>
                <Label>Descrição</Label>
                <Textarea
                  value={flowDescription}
                  onChange={(e) => setFlowDescription(e.target.value)}
                  placeholder="Descreva o propósito deste fluxo"
                  rows={2}
                />
              </div>

              <div>
                <Label>Conexão WhatsApp (Extra Oficial) *</Label>
                <Select
                  value={selectedConnectionId?.toString()}
                  onValueChange={(value) => setSelectedConnectionId(parseInt(value))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione uma conexão" />
                  </SelectTrigger>
                  <SelectContent>
                    {connections.map(conn => (
                      <SelectItem key={conn.id} value={conn.id.toString()}>
                        {conn.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {connections.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    Nenhuma conexão Extra Oficial encontrada. Configure uma conexão primeiro.
                  </p>
                )}
              </div>
            </div>

            {/* Mensagens */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Mensagens do Fluxo</Label>
                <Button onClick={addMessage} size="sm" variant="outline">
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar Mensagem
                </Button>
              </div>

              {messages.length === 0 && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Nenhuma mensagem adicionada. Clique em "Adicionar Mensagem" para começar.
                  </AlertDescription>
                </Alert>
              )}

              {messages.map((message, index) => (
                <Card key={message.order} className="border-l-4 border-l-blue-500">
                  <CardContent className="pt-4">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Badge variant="outline">Mensagem {message.order}</Badge>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => moveMessage(message.order, 'up')}
                            disabled={index === 0}
                          >
                            <ArrowUp className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => moveMessage(message.order, 'down')}
                            disabled={index === messages.length - 1}
                          >
                            <ArrowDown className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeMessage(message.order)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      <div>
                        <Label>Tipo</Label>
                        <Select
                          value={message.type}
                          onValueChange={(value: 'TEXT' | 'FILE' | 'IMAGE') => updateMessage(message.order, 'type', value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="TEXT">Texto</SelectItem>
                            <SelectItem value="IMAGE">Imagem (upload)</SelectItem>
                            <SelectItem value="FILE">Arquivo (URL do boleto)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label>Conteúdo da Mensagem</Label>
                        <Textarea
                          value={message.content}
                          onChange={(e) => updateMessage(message.order, 'content', e.target.value)}
                          placeholder="Digite a mensagem. Use {variavel} para inserir dados dinâmicos."
                          rows={3}
                        />
                        <div className="flex items-center gap-2 mt-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setShowVariableHelper(!showVariableHelper)}
                          >
                            <Sparkles className="w-3 h-3 mr-1" />
                            Variáveis Disponíveis
                          </Button>
                        </div>

                        {showVariableHelper && (
                          <div className="mt-2 p-3 bg-gray-50 rounded-lg max-h-48 overflow-y-auto">
                            <p className="text-xs font-medium mb-2">Clique para inserir:</p>
                            <div className="grid grid-cols-2 gap-2">
                              {availableVariables.map(v => (
                                <Button
                                  key={v.name}
                                  size="sm"
                                  variant="ghost"
                                  className="justify-start text-xs h-auto py-1"
                                  onClick={() => {
                                    insertVariable(message.order, v.name);
                                    setShowVariableHelper(false);
                                  }}
                                >
                                  <code className="font-mono">{`{${v.name}}`}</code>
                                </Button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {message.type === 'IMAGE' && (
                        <div className="space-y-2">
                          <Label>Imagem</Label>
                          <div className="flex gap-2 items-center">
                            <label className={`cursor-pointer inline-flex ${uploadingImageForOrder === message.order ? 'pointer-events-none opacity-60' : ''}`}>
                              <input
                                type="file"
                                accept="image/jpeg,image/png,image/gif,image/webp"
                                className="hidden"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) handleUploadImage(message.order, file);
                                  e.target.value = '';
                                }}
                                disabled={uploadingImageForOrder === message.order}
                              />
                              <span className="inline-flex items-center px-3 py-2 text-sm font-medium border rounded-md hover:bg-gray-50">
                                <Upload className="w-4 h-4 mr-1" />
                                {uploadingImageForOrder === message.order ? 'Enviando...' : 'Enviar imagem'}
                              </span>
                            </label>
                          </div>
                          {message.image_url && (
                            <div className="text-xs text-green-600 truncate" title={message.image_url}>
                              ✓ Imagem: {message.image_url}
                            </div>
                          )}
                          <Input
                            placeholder="Ou cole a URL da imagem (https://...)"
                            value={message.image_url || ''}
                            onChange={(e) => updateMessage(message.order, 'image_url', e.target.value)}
                            className="text-sm"
                          />
                        </div>
                      )}

                      {message.type === 'FILE' && (
                        <div>
                          <Label>Coluna do Arquivo (URL)</Label>
                          <Select
                            value={message.file_column}
                            onValueChange={(value) => updateMessage(message.order, 'file_column', value)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione a coluna" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="link_boleto">link_boleto (PDF do Boleto)</SelectItem>
                              <SelectItem value="link_pix">link_pix (QR Code PIX)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      <div>
                        <Label>Delay após esta mensagem (segundos)</Label>
                        <Input
                          type="number"
                          min="0"
                          value={message.delay_after_seconds}
                          onChange={(e) => updateMessage(message.order, 'delay_after_seconds', parseInt(e.target.value))}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Configurações Globais */}
            {messages.length > 0 && (
              <Card className="bg-purple-50 border-purple-200">
                <CardHeader>
                  <CardTitle className="text-sm">Configurações Globais</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Delay entre envios completos (segundos)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={delayBetweenSends}
                      onChange={(e) => setDelayBetweenSends(parseInt(e.target.value))}
                    />
                    <p className="text-xs text-gray-600 mt-1">
                      Tempo de espera após concluir todas as mensagens do fluxo antes de iniciar um novo envio
                    </p>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={isActive}
                      onCheckedChange={setIsActive}
                    />
                    <Label>Fluxo Ativo</Label>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Ações */}
            <div className="flex items-center gap-3 pt-4 border-t">
              <Button onClick={saveFlow} disabled={loading}>
                <Save className="w-4 h-4 mr-2" />
                {isCreatingNew ? 'Criar Fluxo' : 'Salvar Alterações'}
              </Button>

              {!isCreatingNew && selectedFlowId && (
                <>
                  <Button onClick={generatePreview} variant="outline" disabled={loading}>
                    <Eye className="w-4 h-4 mr-2" />
                    Preview
                  </Button>

                  <Button onClick={deleteFlow} variant="destructive" disabled={loading}>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Excluir
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Preview */}
      {preview && (
        <Card className="border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-600" />
              Preview do Fluxo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {preview.messages?.map((msg: any, idx: number) => (
                <div key={idx} className="p-3 bg-white rounded-lg border">
                  <div className="flex items-start justify-between mb-2">
                    <Badge variant="outline">Mensagem {idx + 1}</Badge>
                    <Badge variant={msg.type === 'TEXT' ? 'default' : 'secondary'}>
                      {msg.type}
                    </Badge>
                  </div>
                  <p className="text-sm whitespace-pre-wrap">{(msg as any).interpolated_content || msg.content}</p>
                  {((msg as any).file_url || (msg as any).fileUrl) && (
                    <div className="mt-2 text-xs text-blue-600">
                      📎 Arquivo: {(msg as any).file_url || (msg as any).fileUrl}
                    </div>
                  )}
                  {msg.image_url && (
                    <div className="mt-2 text-xs text-blue-600">
                      🖼️ Imagem: {msg.image_url}
                    </div>
                  )}
                  {msg.delay_after_seconds > 0 && (
                    <div className="mt-2 flex items-center gap-1 text-xs text-gray-500">
                      <Clock className="w-3 h-3" />
                      Aguardar {msg.delay_after_seconds}s
                    </div>
                  )}
                </div>
              ))}
              <Alert>
                <Clock className="h-4 w-4" />
                <AlertDescription>
                  Delay entre envios completos: {preview.delay_between_sends_seconds}s
                </AlertDescription>
              </Alert>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
